/**
 * ProctoredInterviewTab — "Proctored Interview" tab on the Job detail page.
 *
 * Flow: no config yet -> Create -> Extract Context -> edit Skills & Rubrics
 * (max 6 skills, defaults pre-filled from extraction), each skill carrying
 * its own question type/subtype mix (how many of each, and their timing) ->
 * Generate Questions (question bank, shared by every candidate matched to
 * this job). Once active, every submission for this job gets a public
 * candidate link here, plus a scorecard once completed.
 */

import React, { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { toast } from 'react-hot-toast'
import {
  SparklesIcon, PlusIcon, TrashIcon, EnvelopeIcon, ClipboardDocumentIcon, DocumentTextIcon,
  CheckCircleIcon, ClipboardDocumentCheckIcon, PencilSquareIcon, ArrowPathIcon, ClockIcon, XMarkIcon, ExclamationCircleIcon,
  CodeBracketIcon, Square3Stack3DIcon, MicrophoneIcon, MinusIcon,
  ChevronDownIcon,
} from '@heroicons/react/24/outline'
import InfoTooltip from '@/components/common/InfoTooltip'
import { usePermission } from '@/hooks/usePermission'

import LoadingSpinner from '@/components/common/LoadingSpinner'
import {
  getConfigForJob, createConfig, extractContext, updateConfig, generateQuestions,
  listSubmissions, getAuditLog, sendCandidateEmail, reissueSession,
  type SkillRubric, type SubmissionSession, type AuditLogEntry, type RubricCriterion,
} from '@/services/proctoredInterviewApi'
import { fetchQuestionTypes, type QuestionType } from '@/services/questionTypeApi'
import { fetchComplexityLevels, type ComplexityLevel } from '@/services/complexityLevelApi'
import QuestionBankEditor from './QuestionBankEditor'

interface ProctoredInterviewTabProps {
  jobId: string
  jobTitle?: string
  isActive?: boolean
}

const MAX_SKILLS = 6

export default function ProctoredInterviewTab({ jobId, jobTitle, isActive }: ProctoredInterviewTabProps) {
  const navigate = useNavigate()
  const queryClient = useQueryClient()
  // Added 2026-10-07 — gates every create/edit/generate/send/reissue action
  // in this tab. Someone holding only proctoredInterviews.view (not .manage)
  // can still see this tab (OR-based tab visibility, unchanged) and the
  // read-only parts (config summary, audit log, submission statuses, view
  // scorecard/result) — but none of the actions that change state. Hidden,
  // not disabled, per the project's own convention for this.
  const { can } = usePermission()
  const canManage = can('proctoredInterviews.manage')
  const [editingSkills, setEditingSkills] = useState(false)
  const [showHistory, setShowHistory] = useState(false)
  // Shown once, right after an extraction actually seeds question counts (see
  // extractMutation below) — extraction is a no-op on skills_rubrics once any
  // skill exists (backend: "if not config.skills_rubrics"), so this only ever
  // fires on a genuinely fresh config, which is exactly when a recruiter
  // needs the "these numbers came from the AI, not from you" context. Lives
  // here (not inside ConfigWizard) because ConfigWizard remounts on every
  // config change, including a plain "Save Progress" — this flag must
  // survive that and only be cleared by an explicit dismiss.
  const [justExtractedMix, setJustExtractedMix] = useState(false)
  // True while the Skills & Rubrics editor has edits not yet saved — adding a
  // question reloads that editor from the last save, so the Question Bank
  // warns before discarding them.
  const [skillsDirty, setSkillsDirty] = useState(false)

  const { data: config, isLoading: configLoading, isFetching: configFetching } = useQuery({
    queryKey: ['proctored-interview-config', jobId],
    queryFn: () => getConfigForJob(jobId),
    refetchOnMount: 'always',
    staleTime: 0,
  })

  const { data: questionTypes } = useQuery({
    queryKey: ['question-types-active'],
    queryFn: () => fetchQuestionTypes(false),
  })

  // Without complexityLevels.view the picker is simply not shown (no levels).
  const { data: complexityLevels } = useQuery({
    queryKey: ['complexity-levels'],
    queryFn: fetchComplexityLevels,
    enabled: can('complexityLevels.view'),
  })

  const { data: submissions, isLoading: submissionsLoading } = useQuery({
    queryKey: ['proctored-interview-submissions', jobId],
    queryFn: () => listSubmissions(jobId),
    enabled: config?.status === 'active',
    refetchOnMount: 'always',
  })

  // Generates a fresh link for one interrupted candidate. Refetches the whole
  // submissions list rather than patching the row in place: the new attempt
  // changes status, attempt_number, can_reissue AND result_session_id
  // together, and the server is the authority on all four.
  const reissueMut = useMutation({
    mutationFn: (sessionId: string) => reissueSession(sessionId),
    onSuccess: (updated) => {
      queryClient.invalidateQueries({ queryKey: ['proctored-interview-submissions', jobId] })
      toast.success(`New interview link generated for ${updated.candidate_name || 'the candidate'}.`)
    },
    onError: () => {
      // api.ts's response interceptor already toasts 403s, so this only needs
      // to cover the cap-reached / wrong-status 400s.
      toast.error('Could not generate a new link. Please refresh and try again.')
    },
  })

  const configQueryKey = ['proctored-interview-config', jobId]
  const invalidateConfig = () => queryClient.invalidateQueries({ queryKey: configQueryKey })
  // Every config-mutating endpoint already returns the fresh config in its
  // response — write it straight into the query cache instead of only
  // invalidating and waiting on a background refetch round-trip. This is
  // what actually makes the UI update "then and there": invalidate alone
  // still leaves a window (however brief) where the component re-renders
  // with the old cached config before the refetch resolves, and combined
  // with the ConfigWizard remount-key fix below, this closes that gap
  // completely instead of just narrowing it.
  const applyConfig = (config: any) => {
    queryClient.setQueryData(configQueryKey, config)
    invalidateConfig()
  }

  const createMutation = useMutation({
    mutationFn: () => createConfig(jobId),
    onSuccess: (config) => {
      applyConfig(config)
      extractMutation.mutate(config.id)
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to create interview'),
  })

  const extractMutation = useMutation({
    mutationFn: (configId: string) => extractContext(configId),
    onSuccess: (config) => {
      toast.success('Context extracted from job description')
      applyConfig(config)
      setEditingSkills(true)
      setJustExtractedMix((config.skills_rubrics || []).some((s: SkillRubric) => (s.question_mix || []).length > 0))
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to extract context'),
  })

  const saveConfigMutation = useMutation({
    mutationFn: ({ configId, data }: { configId: string; data: Partial<{ skills_rubrics: SkillRubric[] }> }) =>
      updateConfig(configId, data),
    onSuccess: (config) => { applyConfig(config); toast.success('Progress saved') },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to save'),
  })

  const generateMutation = useMutation({
    mutationFn: async ({ configId, data }: { configId: string; data: Partial<{ skills_rubrics: SkillRubric[] }> }) => {
      // Persist the wizard's current skills/mix first and only generate
      // against what was actually saved — mutateAsync so this awaits the
      // PUT instead of racing it (generate-questions reads from the DB).
      await updateConfig(configId, data)
      return generateQuestions(configId)
    },
    onSuccess: (result) => {
      applyConfig(result.config)
      setEditingSkills(false)
      if (result.coverage_warnings.length > 0) {
        toast(`Questions generated, but some rubric criteria may not be fully covered:\n${result.coverage_warnings.join('\n')}`, { icon: '⚠️', duration: 8000 })
      } else {
        toast.success('Question bank generated')
      }
    },
    onError: (e: any) => toast.error(e?.response?.data?.detail || 'Failed to generate questions'),
  })

  if (configLoading) {
    return <div className="flex justify-center py-12"><LoadingSpinner size="lg" /></div>
  }

  // ------------------------------------------------------------------
  // No config yet
  // ------------------------------------------------------------------
  if (!config) {
    return (
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-12 text-center">
        <div className="w-14 h-14 rounded-full bg-blue-50 flex items-center justify-center mx-auto mb-4">
          <SparklesIcon className="w-7 h-7 text-blue-500" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">No Proctored Assessment Yet</h3>
        <p className="text-sm text-gray-500 mt-1.5 mb-5 max-w-md mx-auto">
          {canManage
            ? 'Create one to extract context from this job description, choose skills & rubrics, and generate a shared question bank every matched candidate can take via a public link.'
            : "No Proctored Assessment has been set up for this job yet."}
        </p>
        {canManage && (
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm"
          >
            {createMutation.isPending ? <LoadingSpinner size="sm" /> : <PlusIcon className="w-4 h-4" />}
            {createMutation.isPending ? 'Creating…' : 'Create Interview'}
          </button>
        )}
      </div>
    )
  }

  // Gated on canManage — the wizard is pure editing surface (extract/save/
  // generate), so a view-only role never sees it, even for a draft config.
  const showWizard = canManage && (config.status === 'draft' || editingSkills)

  return (
    <div className="space-y-6">
      {configFetching && !configLoading && (
        <div className="flex items-center gap-1.5 text-xs text-blue-600">
          <LoadingSpinner size="sm" /> Updating…
        </div>
      )}

      {/* View-only role looking at a config someone else hasn't finished
          setting up yet — showWizard is false for them even in draft
          status, so without this they'd see a blank gap here. */}
      {!canManage && config.status === 'draft' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8 text-center">
          <p className="text-sm text-gray-500">Setup for this Proctored Assessment is still in progress.</p>
        </div>
      )}

      {showWizard && (
        <ConfigWizard
          // Remounts (fresh local state) whenever the server-side config
          // actually changes — extract-context/save/generate all bump
          // updated_at. Without this, the wizard's skills/mix state was
          // captured once on mount and never resynced, so a freshly
          // extracted context's auto-filled skills (or a regenerated
          // question bank) only appeared after a hard page refresh.
          key={`${config.id}-${config.updated_at}`}
          configId={config.id}
          jobTitle={jobTitle}
          jdContext={config.jd_context}
          contextExtractedAt={config.context_extracted_at}
          skillsRubrics={config.skills_rubrics}
          questionTypes={questionTypes || []}
          complexityLevels={complexityLevels || []}
          onExtractContext={() => extractMutation.mutate(config.id)}
          extracting={extractMutation.isPending}
          onSave={(data) => saveConfigMutation.mutate({ configId: config.id, data })}
          saving={saveConfigMutation.isPending}
          onGenerate={(data) => generateMutation.mutate({ configId: config.id, data })}
          generating={generateMutation.isPending}
          onCancelEdit={config.status === 'active' ? () => setEditingSkills(false) : undefined}
          justExtractedMix={justExtractedMix}
          onDismissJustExtractedMix={() => setJustExtractedMix(false)}
          onDirtyChange={setSkillsDirty}
        />
      )}

      {!showWizard && config.status === 'active' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 px-5 py-4">
          <div className="flex items-center justify-between gap-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className="w-7 h-7 rounded-full bg-green-50 flex items-center justify-center flex-shrink-0">
                <CheckCircleIcon className="w-4 h-4 text-green-600" />
              </div>
              <div className="min-w-0">
                <h3 className="text-sm font-semibold text-gray-900">Proctored Assessment Configured</h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {config.questions.length} questions · {config.skills_rubrics.length} skills
                </p>
              </div>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              {canManage && (
                <InfoTooltip size="md" text="Candidates who already started an interview keep the questions from the version they started with — editing here only affects candidates who haven't started yet." />
              )}
              <button
                onClick={() => setShowHistory(true)}
                title="Audit"
                aria-label="Audit"
                className="inline-flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentCheckIcon className="w-5 h-5" />
              </button>
              {canManage && (
                <button
                  onClick={() => setEditingSkills(true)}
                  title="Edit / Regenerate"
                  aria-label="Edit / Regenerate"
                  className="inline-flex items-center justify-center w-9 h-9 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50"
                >
                  <span className="relative inline-flex items-center justify-center w-5 h-5">
                    <PencilSquareIcon className="w-5 h-5" />
                    <ArrowPathIcon className="w-3 h-3 absolute -bottom-1 -right-1 bg-white rounded-full text-blue-600" />
                  </span>
                </button>
              )}
            </div>
          </div>
          <div className="flex flex-wrap gap-2 mt-3">
            {config.skills_rubrics.map((s) => (
              <span key={s.skill_name} className="inline-flex items-center gap-1.5 text-xs font-medium px-2.5 py-1 bg-blue-50 text-blue-700 border border-blue-200 rounded-md">
                <span className="w-1.5 h-1.5 rounded-full bg-blue-400 flex-shrink-0" />
                {s.skill_name}
              </span>
            ))}
          </div>
        </div>
      )}

      {showHistory && (
        <HistoryModal configId={config.id} onClose={() => setShowHistory(false)} />
      )}

      {showWizard && config.skills_rubrics.length > 0 && (
        <div className="space-y-2">
          {config.questions.length > 0 && (
            <p className="text-xs text-amber-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-2">
              Clicking "Generate Question Bank" above replaces every question below with a fresh AI-generated set —
              edit or add questions here only when you're done regenerating.
            </p>
          )}
          <QuestionBankEditor
            configId={config.id}
            questions={config.questions}
            skillsRubrics={config.skills_rubrics}
            questionTypes={questionTypes || []}
            complexityLevels={complexityLevels || []}
            hasUnsavedSkillChanges={skillsDirty}
          />
        </div>
      )}

      {config.status === 'active' && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50">
            <h3 className="text-sm font-semibold text-gray-900">Candidate Links &amp; Scorecards</h3>
            <p className="text-xs text-gray-500 mt-0.5">
              Every candidate matched to this job gets an interview link — click Send Email to email it to them directly.
            </p>
          </div>
          {submissionsLoading ? (
            <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
          ) : !submissions || submissions.length === 0 ? (
            <p className="px-5 py-8 text-sm text-gray-500 text-center">
              No candidates matched to this job yet. Match candidates from the Apply Matching tab.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {submissions.map((s) => (
                <SubmissionRow
                  key={s.job_match_id}
                  submission={s}
                  canManage={canManage}
                  onViewScorecard={() => navigate(`/proctored-interview/scorecard/${s.session_id}`)}
                  onViewResult={() => {
                    if (s.result_session_id) navigate(`/proctored-interview/scorecard/${s.result_session_id}`)
                  }}
                  onReissue={() => reissueMut.mutate(s.session_id)}
                  reissuing={reissueMut.isPending && reissueMut.variables === s.session_id}
                />
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  )
}

// ============================================================================
// Submission row
// ============================================================================

// Running total of a skill's criterion weights. The backend rejects a save
// unless this is exactly 100, so the recruiter needs to see the arithmetic
// while editing rather than discovering it in an error toast.
function RubricWeightTotal({ criteria }: { criteria: RubricCriterion[] }) {
  const weights = criteria.map((c) => c.weight).filter((w): w is number => typeof w === 'number')
  // A config saved before weighting existed has none at all. That is valid —
  // it scores by equal split — so say nothing rather than flag it as broken.
  if (weights.length === 0) return null

  const total = Math.round(weights.reduce((a, b) => a + b, 0) * 10) / 10
  const complete = weights.length === criteria.length
  const valid = complete && Math.abs(total - 100) < 0.01

  return (
    <span className={`text-xs font-medium ${valid ? 'text-gray-400' : 'text-amber-600'}`}>
      {!complete
        ? 'Every criterion needs a weight'
        : valid
          ? 'Weights total 100'
          : `Weights total ${total} — must be 100`}
    </span>
  )
}

function SubmissionRow({
  submission, canManage, onViewScorecard, onViewResult, onReissue, reissuing,
}: {
  submission: SubmissionSession
  // Gates the entire "invite not yet completed" cluster — status badge,
  // Sent indicator, Send/Resend Email, Copy Interview Link, Generate New
  // Link — per the user's explicit 2026-10-08 request: without Manage, none
  // of that invite-lifecycle chatter should show, even though Copy Link
  // itself calls no API. View Scorecard/Result stay ungated regardless —
  // those are the actual submission data proctoredInterviews.view exists
  // to grant, not the invite-management lifecycle this cluster is about.
  canManage: boolean
  onViewScorecard: () => void
  // Opens the scorecard of the most recent attempt that finished. Distinct
  // from onViewScorecard, which always opens the latest attempt: after a
  // re-issue the latest is `pending` and has no scorecard yet, so this points
  // back at the interrupted attempt behind it.
  onViewResult: () => void
  onReissue: () => void
  reissuing: boolean
}) {
  const [sending, setSending] = useState(false)
  // Seeded from the backend (persists across reloads) and updated in place the moment a send
  // succeeds, so "Send Email" swaps to "Resend Email" without waiting for the submissions list
  // to refetch.
  const [sentAt, setSentAt] = useState<string | null>(submission.invite_email_sent_at || null)

  const sendEmail = async () => {
    if (!submission.candidate_email) {
      toast.error('This candidate has no email on file.')
      return
    }
    setSending(true)
    try {
      const result = await sendCandidateEmail(submission.session_id)
      setSentAt(result.sent_at || new Date().toISOString())
      toast.success(sentAt ? 'Interview invitation re-sent' : (result.message || 'Interview invitation emailed'))
    } catch (e: any) {
      toast.error(e?.response?.data?.detail || 'Failed to send the email')
    } finally {
      setSending(false)
    }
  }

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(submission.public_url)
      toast.success('Interview link copied')
    } catch {
      toast.error('Could not copy link — copy it manually')
    }
  }

  const statusLabel: Record<string, string> = {
    pending: 'Link not opened',
    verified: 'Verified, not started',
    in_progress: 'In progress',
    completed: 'Completed',
    incomplete: 'Incomplete',
  }
  const statusColor: Record<string, string> = {
    pending: 'bg-gray-100 text-gray-600',
    verified: 'bg-amber-50 text-amber-700',
    in_progress: 'bg-blue-50 text-blue-700',
    completed: 'bg-green-50 text-green-700',
    // Deliberately a step darker than `verified` above so the two amber pills
    // are not mistaken for each other at a glance.
    incomplete: 'bg-amber-100 text-amber-800',
  }

  const initials = (submission.candidate_name || '?').trim().split(/\s+/).slice(0, 2).map((p) => p[0]?.toUpperCase()).join('') || '?'

  return (
    <div className="flex items-center justify-between px-6 py-3.5 hover:bg-gray-50 transition-colors">
      <div className="min-w-0 flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-blue-100 text-blue-700 text-xs font-semibold flex items-center justify-center flex-shrink-0">
          {initials}
        </div>
        <div className="min-w-0">
          <p className="text-sm font-medium text-gray-900 truncate">{submission.candidate_name || 'Unnamed Candidate'}</p>
          <p className="text-xs text-gray-500 truncate">{submission.candidate_email}</p>
        </div>
      </div>
      <div className="flex items-center gap-3 flex-shrink-0">
        {/* History first, current state to its right — the row reads
            chronologically. Only rendered once an attempt has actually been
            interrupted; a candidate who completed normally never sees this,
            because their finished attempt IS the latest one and is served by
            the green View Scorecard button below instead. */}
        {submission.status !== 'completed' && submission.result_session_id && (
          <button
            onClick={onViewResult}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-amber-500 text-white rounded-md text-xs font-medium hover:bg-amber-600"
          >
            <DocumentTextIcon className="w-3.5 h-3.5" /> View Result
          </button>
        )}
        {canManage && (
          <span className={`text-xs px-2 py-0.5 rounded-full ${statusColor[submission.status]}`}>
            {statusLabel[submission.status]}
          </span>
        )}
        {submission.auto_submitted_due_to_violations && (
          <span
            title={`Auto-submitted due to suspected malpractice: ${submission.violation_summary || 'proctoring violations detected'}`}
            className="inline-flex items-center text-red-600 cursor-help"
          >
            <ExclamationCircleIcon className="w-5 h-5" />
          </span>
        )}
        {submission.status === 'completed' ? (
          <button
            onClick={onViewScorecard}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-green-600 text-white rounded-md text-xs font-medium hover:bg-green-700"
          >
            <DocumentTextIcon className="w-3.5 h-3.5" /> View Scorecard
          </button>
        ) : (
          <div className="flex items-center gap-2">
            {canManage && sentAt && (
              <span
                title={`Last sent ${new Date(sentAt).toLocaleString()}`}
                className="inline-flex items-center gap-1 text-[11px] text-green-700"
              >
                <CheckCircleIcon className="w-3.5 h-3.5" /> Sent
              </span>
            )}
            {canManage && (
              <button
                onClick={sendEmail}
                disabled={sending}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50"
              >
                {sending ? <LoadingSpinner size="sm" /> : <EnvelopeIcon className="w-3.5 h-3.5" />}
                {sending ? 'Sending…' : sentAt ? 'Resend Email' : 'Send Email'}
              </button>
            )}
            {/* Copy Link is pointless once this attempt is incomplete — that
                link only ever shows the Assessment Interrupted screen now.
                Gated on canManage 2026-10-08 at the user's explicit request —
                previously left available to view-only roles since it calls
                no API, but the user wants the whole invite cluster hidden
                without Manage, not just the state-changing parts of it. */}
            {canManage && submission.status !== 'incomplete' && (
              <button
                onClick={copyLink}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50"
              >
                <ClipboardDocumentIcon className="w-3.5 h-3.5" /> Copy Interview Link
              </button>
            )}
            {canManage && submission.can_reissue && (
              <button
                onClick={onReissue}
                disabled={reissuing}
                className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-40"
              >
                <ArrowPathIcon className="w-3.5 h-3.5" />
                {reissuing ? 'Generating…' : 'Generate New Link'}
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// History (audit log) modal — who created/edited this interview, and when
// ============================================================================

const AUDIT_ACTION_LABELS: Record<string, string> = {
  created: 'Created the interview',
  context_extracted: 'Extracted context from the job description',
  skills_updated: 'Updated skills & rubrics',
  questions_generated: 'Generated the question bank',
  question_added: 'Added a question',
  question_updated: 'Edited a question',
  question_deleted: 'Deleted a question',
}

function auditDetailLine(entry: AuditLogEntry): string | null {
  const d = entry.details || {}
  switch (entry.action) {
    case 'questions_generated':
      return `${d.question_count ?? '?'} questions · ~${d.total_duration_minutes ?? '?'} min`
    case 'skills_updated':
      return Array.isArray(d.skills) ? d.skills.join(', ') : null
    case 'question_added':
      return d.question_text || null
    case 'question_updated':
      return Array.isArray(d.fields) ? `Changed: ${d.fields.join(', ')}` : null
    case 'context_extracted':
      return d.role_title || null
    default:
      return null
  }
}

function HistoryModal({ configId, onClose }: { configId: string; onClose: () => void }) {
  const { data: entries, isLoading } = useQuery({
    queryKey: ['proctored-interview-audit-log', configId],
    queryFn: () => getAuditLog(configId),
  })

  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-lg max-w-lg w-full max-h-[80vh] overflow-y-auto p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900">Interview History</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><XMarkIcon className="w-5 h-5" /></button>
        </div>
        {isLoading ? (
          <div className="flex justify-center py-8"><LoadingSpinner size="md" /></div>
        ) : !entries || entries.length === 0 ? (
          <p className="text-sm text-gray-500 text-center py-8">No history yet.</p>
        ) : (
          <div className="space-y-3">
            {entries.map((entry, idx) => {
              const detail = auditDetailLine(entry)
              return (
                <div key={idx} className="flex gap-3 text-sm">
                  <span className="text-xs px-1.5 py-0.5 bg-gray-100 text-gray-500 rounded font-mono flex-shrink-0 mt-0.5">v{entry.version}</span>
                  <div className="min-w-0">
                    <p className="text-gray-900">{AUDIT_ACTION_LABELS[entry.action] || entry.action}</p>
                    {detail && <p className="text-xs text-gray-500 truncate">{detail}</p>}
                    <p className="text-xs text-gray-400">
                      {entry.changed_by_name || 'Unknown user'} · {new Date(entry.created_at).toLocaleString(undefined, {
                        year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit',
                      })}
                    </p>
                  </div>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </div>
  )
}

// ============================================================================
// Config wizard (context -> skills, rubrics & questions -> generate)
// ============================================================================

interface ConfigWizardProps {
  configId: string
  jobTitle?: string
  jdContext: Record<string, any>
  contextExtractedAt?: string | null
  skillsRubrics: SkillRubric[]
  questionTypes: QuestionType[]
  complexityLevels: ComplexityLevel[]
  onExtractContext: () => void
  extracting: boolean
  onSave: (data: Partial<{ skills_rubrics: SkillRubric[] }>) => void
  saving: boolean
  onGenerate: (data: { skills_rubrics: SkillRubric[] }) => void
  generating: boolean
  onCancelEdit?: () => void
  // Shown as a dismissible banner right after a fresh extraction actually
  // seeded question_mix counts — see ProctoredInterviewTab's extractMutation.
  justExtractedMix: boolean
  onDismissJustExtractedMix: () => void
  // Reports whether the editor holds changes not yet saved (see QuestionBankEditor's warning).
  onDirtyChange: (dirty: boolean) => void
}

function skillMinutes(skill: SkillRubric, questionTypes: QuestionType[]): number {
  return skill.question_mix.reduce((sum, entry) => {
    const type = questionTypes.find((t) => t.id === entry.question_type_id)
    const subtype = type?.subtypes.find((s) => s.id === entry.question_subtype_id)
    const duration = subtype?.default_duration_minutes ?? type?.default_duration_minutes ?? 10
    return sum + duration * entry.count
  }, 0)
}

function skillQuestionCount(skill: SkillRubric): number {
  return skill.question_mix.reduce((sum, entry) => sum + (entry.count || 0), 0)
}

// Visual identity per question-type category, so the "Questions for this
// skill" grid reads at a glance instead of as an undifferentiated list of
// names — a recruiter scanning several skill cards can tell "this one has a
// coding question" from the icon/color alone. Falls back to a neutral chat
// icon for any category an org has customized away from the coding/design/
// verbal defaults (category is a free-text display grouping, not an enum —
// see database/models.py QuestionType.category). Same palette Live
// Interview's LiveInterviewTab.tsx uses for its own question-mix chips.
const CATEGORY_META: Record<string, { icon: typeof CodeBracketIcon; classes: string }> = {
  coding: { icon: CodeBracketIcon, classes: 'bg-violet-50 text-violet-700 border-violet-200' },
  design: { icon: Square3Stack3DIcon, classes: 'bg-amber-50 text-amber-700 border-amber-200' },
  verbal: { icon: MicrophoneIcon, classes: 'bg-emerald-50 text-emerald-700 border-emerald-200' },
}

function categoryMeta(category?: string) {
  return CATEGORY_META[(category || '').toLowerCase()] || { icon: DocumentTextIcon, classes: 'bg-gray-50 text-gray-600 border-gray-200' }
}

function normalizeSkillWords(name: string): string[] {
  return name.toLowerCase().replace(/[^a-z0-9]+/g, ' ').split(' ').filter(Boolean)
}

// Two skill names came from two DIFFERENT extraction runs (an old, already-
// configured skill vs. a fresh jd_context) and often differ slightly in
// phrasing even when they mean the same skill — e.g. "SQL (Oracle SQL,
// sqlplus)" vs "SQL (Oracle/sqlplus)". An exact match misses these. Checks
// whether at least 70% of the SHORTER name's words appear in the other.
function skillNamesMatch(a: string, b: string): boolean {
  const wa = normalizeSkillWords(a)
  const wb = normalizeSkillWords(b)
  if (wa.length === 0 || wb.length === 0) return false
  if (wa.join(' ') === wb.join(' ')) return true
  const [shorter, longer] = wa.length <= wb.length ? [wa, wb] : [wb, wa]
  const longerSet = new Set(longer)
  const overlap = shorter.filter((w) => longerSet.has(w)).length
  return overlap / shorter.length >= 0.7
}

// Compact −/count/+ stepper used by every question-mix chip, instead of a
// bare <input type=number> — the small buttons make "how many of this type"
// a one-click adjustment rather than requiring the recruiter to type over a
// selected value.
function MixStepper({
  count, onChange, max = 20, disableIncrement = false,
}: { count: number; onChange: (next: number) => void; max?: number; disableIncrement?: boolean }) {
  return (
    <div className="flex items-center gap-1.5 flex-shrink-0 ml-1">
      <button
        onClick={() => onChange(Math.max(0, count - 1))}
        disabled={count <= 0}
        className="w-5 h-5 flex items-center justify-center rounded bg-black/5 hover:bg-black/10 disabled:opacity-30"
      >
        <MinusIcon className="w-3 h-3" />
      </button>
      {/* A plain, always-rendered number — not an <input> — so the count can
          never be silently invisible behind any browser/controlled-input
          quirk. Click-to-type isn't needed at this range (0-20); the +/-
          buttons are the only input method. */}
      <span className="w-4 text-center text-sm font-bold text-gray-900 tabular-nums select-none">
        {count}
      </span>
      <button
        onClick={() => onChange(Math.min(max, count + 1))}
        disabled={disableIncrement}
        title={disableIncrement ? "Not suggested for this skill" : undefined}
        className="w-5 h-5 flex items-center justify-center rounded bg-black/5 hover:bg-black/10 disabled:opacity-30 disabled:cursor-not-allowed"
      >
        <PlusIcon className="w-3 h-3" />
      </button>
    </div>
  )
}

// Complexity picker shown under a question-mix row once it has a count —
// which level (see Settings > Complexity Levels) those questions are pitched
// at. Empty value = "Any" (pitched at the role's seniority). Levels are
// ordered easiest -> hardest, so the pill's colour and the 1-3 strength bars
// follow that order and read at a glance without opening the menu.
const LEVEL_TONES = [
  'bg-emerald-50 text-emerald-700 border-emerald-200',
  'bg-amber-50 text-amber-700 border-amber-200',
  'bg-rose-50 text-rose-700 border-rose-200',
]

function LevelBars({ filled }: { filled: number }) {
  return (
    <span className="flex items-end gap-[2px] h-2.5" aria-hidden>
      {[1, 2, 3].map((i) => (
        <span
          key={i}
          className={`w-[3px] rounded-sm ${i <= filled ? 'bg-current' : 'bg-current opacity-20'}`}
          style={{ height: `${i * 33}%` }}
        />
      ))}
    </span>
  )
}

function LevelSelect({
  levels, value, onChange,
}: { levels: ComplexityLevel[]; value?: string | null; onChange: (next: string | null) => void }) {
  if (levels.length === 0) return null
  const index = levels.findIndex((l) => l.id === value)
  const tone = index < 0
    ? 'bg-white text-gray-500 border-gray-200'
    : LEVEL_TONES[Math.min(index, LEVEL_TONES.length - 1)]
  const filled = index < 0 ? 0 : Math.min(3, Math.round(((index + 1) / levels.length) * 3) || 1)
  const selected = index >= 0 ? levels[index] : null
  return (
    <div className="flex items-center justify-between gap-2 w-full">
      <span className="text-[10px] font-semibold uppercase tracking-wide text-gray-400">Complexity</span>
      <label
        className={`relative inline-flex items-center gap-1.5 rounded-full border pl-2 pr-1.5 py-0.5 text-[11px] font-medium cursor-pointer hover:shadow-sm ${tone}`}
        title={selected?.description || 'No specific complexity: pitched at the role\'s seniority'}
      >
        <LevelBars filled={filled} />
        <span className="whitespace-nowrap">{selected ? selected.name : 'Any'}</span>
        <ChevronDownIcon className="w-3 h-3 opacity-60" />
        <select
          value={value || ''}
          onChange={(e) => onChange(e.target.value || null)}
          aria-label="Complexity level for these questions"
          className="absolute inset-0 w-full h-full opacity-0 cursor-pointer"
        >
          <option value="">Any (role's seniority)</option>
          {levels.map((l) => (
            <option key={l.id} value={l.id}>{l.name}</option>
          ))}
        </select>
      </label>
    </div>
  )
}

// Criteria with no weights at all (AI-seeded, or saved before weighting
// existed) score by an equal split anyway — show that split as the default
// instead of empty boxes. Same split as ConfigWizard's rebalanceWeights (any
// remainder goes to the first criterion so the total is exactly 100). Any
// recruiter-entered weight is left untouched.
function withDefaultWeights(criteria: RubricCriterion[]): RubricCriterion[] {
  if (criteria.length === 0 || criteria.some((c) => typeof c.weight === 'number')) return criteria
  const share = Math.floor((100 / criteria.length) * 10) / 10
  const remainder = Math.round((100 - share * criteria.length) * 10) / 10
  return criteria.map((c, i) => ({ ...c, weight: i === 0 ? share + remainder : share }))
}

function ConfigWizard({
  jdContext, contextExtractedAt, skillsRubrics, questionTypes, complexityLevels,
  onExtractContext, extracting, onSave, saving, onGenerate, generating, onCancelEdit,
  justExtractedMix, onDismissJustExtractedMix, onDirtyChange,
}: ConfigWizardProps) {
  const [skills, setSkills] = useState<SkillRubric[]>(
    skillsRubrics.map((s) => ({ ...s, rubric_criteria: withDefaultWeights(s.rubric_criteria || []), question_mix: s.question_mix || [] }))
  )

  // Reconciliation for a config that predates the AI-suggested-mix feature
  // (or a skill added by hand): a skill with no question_mix yet, whose name
  // fuzzy-matches a jd_context suggestion (see skillNamesMatch — handles
  // rephrasing between extraction runs, not just identical names), gets that
  // suggestion's counts applied automatically on mount. Only touches skills
  // whose question_mix is currently empty, so it can never overwrite a
  // recruiter's own edits. Runs once — ConfigWizard remounts (fresh `key`)
  // whenever the server-side config actually changes, so there is no need to
  // re-run this on every render.
  useEffect(() => {
    const candidateSkills = Array.isArray(jdContext?.candidate_skills) ? jdContext.candidate_skills : []
    if (candidateSkills.length === 0) return
    setSkills((prev) => prev.map((s) => {
      if (s.question_mix.length > 0) return s
      const match = candidateSkills.find((c: any) => c?.skill_name && skillNamesMatch(c.skill_name, s.skill_name))
      if (!match) return s
      return { ...s, question_mix: resolveSuggestedQuestionMix(match.suggested_question_mix) }
    }))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
  const [newSkillName, setNewSkillName] = useState('')
  // Whether the "add a skill" combobox's suggestion dropdown is open — see
  // extractedSkillSuggestions below.
  const [showSkillSuggestions, setShowSkillSuggestions] = useState(false)

  const totalMinutesEstimate = skills.reduce((sum, s) => sum + skillMinutes(s, questionTypes), 0)
  const hasAnyQuestions = skills.some((s) => s.question_mix.length > 0)

  // Every skill the last JD extraction identified (up to 14 — see
  // proctored_interview_llm.py's extract_jd_context), minus whichever ones
  // are already in the interview. Only the first MAX_SKILLS (by importance)
  // get auto-seeded when a config is first extracted; the rest surface here
  // so removing/re-adding a skill, or picking one the auto-seed didn't
  // include, still restores its real AI-suggested rubric and question mix
  // instead of starting blank.
  const extractedSkillSuggestions: any[] = Array.isArray(jdContext?.candidate_skills)
    ? jdContext.candidate_skills.filter((c: any) =>
        c?.skill_name && !skills.some((s) => s.skill_name.toLowerCase() === c.skill_name.trim().toLowerCase())
      )
    : []

  const filteredSuggestions = extractedSkillSuggestions.filter((c) =>
    !newSkillName.trim() || c.skill_name.toLowerCase().includes(newSkillName.trim().toLowerCase())
  )

  // Resolves a jd_context candidate_skill's suggested_question_mix (type
  // NAMES only, per proctored_interview_llm.extract_jd_context) against the
  // org's real active question types — same name-matching principle the
  // backend's _seed_question_mix_from_suggestion (proctored_interviews.py)
  // uses, so a skill re-added from suggestions here behaves identically to
  // one seeded straight from a fresh extraction. The AI-suggested mix only
  // ever proposes a top-level question type, never a subtype, so
  // question_subtype_id is always set to null here — matching the backend.
  const resolveSuggestedQuestionMix = (suggested: any[] | undefined): SkillRubric['question_mix'] => {
    const mix: SkillRubric['question_mix'] = []
    for (const entry of suggested || []) {
      const wantedName = (entry?.question_type_name || '').trim().toLowerCase()
      if (!wantedName) continue
      const type = questionTypes.find((t) => t.name.trim().toLowerCase() === wantedName)
      if (!type) continue
      const count = Math.max(0, Math.min(20, Number(entry?.count) || 0))
      if (count <= 0) continue
      mix.push({ question_type_id: type.id, question_subtype_id: null, count })
    }
    return mix
  }

  // Which top-level question types the last JD extraction judged applicable
  // to a given skill (see proctored_interview_llm.py's extract_jd_context
  // prompt: "omit types that don't apply to that skill at all"). Returns
  // null when there's no basis to judge — the skill was never matched
  // against a jd_context suggestion (manually added, or extraction never
  // ran) — in which case every type stays fully editable, same as before
  // this feature existed. A matched skill with an actual (possibly empty)
  // suggested_question_mix returns the real Set of eligible type ids,
  // applied at the top level only — the AI never judges per-subtype.
  const eligibleTypeIdsForSkill = (skill: SkillRubric): Set<string> | null => {
    const candidateSkills = Array.isArray(jdContext?.candidate_skills) ? jdContext.candidate_skills : []
    const match = candidateSkills.find((c: any) => c?.skill_name && skillNamesMatch(c.skill_name, skill.skill_name))
    if (!match || !Array.isArray(match.suggested_question_mix)) return null
    const ids = new Set<string>()
    for (const entry of match.suggested_question_mix) {
      const wantedName = (entry?.question_type_name || '').trim().toLowerCase()
      if (!wantedName) continue
      const type = questionTypes.find((t) => t.name.trim().toLowerCase() === wantedName)
      if (type) ids.add(type.id)
    }
    return ids
  }

  const addSkillFromSuggestion = (candidate: any) => {
    const name = (candidate?.skill_name || '').trim()
    if (!name) return
    if (skills.length >= MAX_SKILLS) { toast.error(`A single interview cannot assess more than ${MAX_SKILLS} skills.`); return }
    if (skills.some((s) => s.skill_name.toLowerCase() === name.toLowerCase())) return
    const rubric_criteria = withDefaultWeights((candidate.suggested_rubric || [])
      .filter((c: any) => c && c.name)
      .map((c: any) => ({ name: c.name, description: c.description })))
    setSkills([...skills, {
      skill_name: name,
      weight: 1,
      rubric_criteria,
      question_mix: resolveSuggestedQuestionMix(candidate.suggested_question_mix),
    }])
    setNewSkillName('')
    setShowSkillSuggestions(false)
  }

  // Overwrites one existing skill's question_mix from a chosen jd_context
  // suggestion — the in-place counterpart to addSkillFromSuggestion, for a
  // skill that's already on the list (seeded before this feature existed, or
  // typed in by hand) rather than one being newly added.
  const applySuggestedMixToSkill = (skillName: string, candidateSkillName: string) => {
    const candidate = (Array.isArray(jdContext?.candidate_skills) ? jdContext.candidate_skills : [])
      .find((c: any) => c?.skill_name === candidateSkillName)
    if (!candidate) return
    const mix = resolveSuggestedQuestionMix(candidate.suggested_question_mix)
    if (mix.length === 0) {
      toast.error(`"${candidateSkillName}" has no suggested question types that match this org's active types.`)
      return
    }
    setSkills(skills.map((s) => (s.skill_name === skillName ? { ...s, question_mix: mix } : s)))
    toast.success(`Applied suggested question mix from "${candidateSkillName}"`)
  }

  const addSkill = () => {
    const name = newSkillName.trim()
    if (!name) return
    if (skills.length >= MAX_SKILLS) { toast.error(`A single interview cannot assess more than ${MAX_SKILLS} skills.`); return }
    if (skills.some((s) => s.skill_name.toLowerCase() === name.toLowerCase())) {
      toast.error('That skill is already in the list.')
      return
    }
    // Falls back to a suggestion with a matching name (if the recruiter typed
    // an extracted skill's name out by hand instead of clicking it in the
    // dropdown) so it still arrives with its real rubric/question-mix
    // suggestions rather than starting completely blank.
    const matchingSuggestion = extractedSkillSuggestions.find(
      (c) => (c.skill_name || '').trim().toLowerCase() === name.toLowerCase()
    )
    if (matchingSuggestion) {
      addSkillFromSuggestion(matchingSuggestion)
      return
    }
    setSkills([...skills, { skill_name: name, weight: 1, rubric_criteria: [], question_mix: [] }])
    setNewSkillName('')
  }

  const removeSkill = (skillName: string) => setSkills(skills.filter((s) => s.skill_name !== skillName))

  // Re-splits a skill's 100 points equally across its criteria, giving any
  // remainder to the first one so the total lands exactly on 100 (three
  // criteria would otherwise be 33.3 x 3 = 99.9, which the backend rejects).
  // Applied whenever criteria are added or removed: leaving the old weights
  // behind would produce an invalid config by default, and the recruiter
  // would have to fix arithmetic they never asked to do.
  const rebalanceWeights = (criteria: RubricCriterion[]): RubricCriterion[] => {
    if (criteria.length === 0) return criteria
    const share = Math.floor((100 / criteria.length) * 10) / 10
    const remainder = Math.round((100 - share * criteria.length) * 10) / 10
    return criteria.map((c, i) => ({ ...c, weight: i === 0 ? share + remainder : share }))
  }

  const addCriterion = (skillName: string) => {
    setSkills(skills.map((s) => s.skill_name === skillName
      ? { ...s, rubric_criteria: rebalanceWeights([...s.rubric_criteria, { name: '', description: '' }]) }
      : s))
  }

  const updateCriterion = (
    skillName: string,
    idx: number,
    field: 'name' | 'description' | 'weight',
    value: string | number | null,
  ) => {
    setSkills(skills.map((s) => s.skill_name === skillName
      ? { ...s, rubric_criteria: s.rubric_criteria.map((c, i) => i === idx ? { ...c, [field]: value } : c) }
      : s))
  }

  const removeCriterion = (skillName: string, idx: number) => {
    setSkills(skills.map((s) => s.skill_name === skillName
      ? { ...s, rubric_criteria: rebalanceWeights(s.rubric_criteria.filter((_, i) => i !== idx)) }
      : s))
  }

  const setSkillMixCount = (skillName: string, typeId: string, subtypeId: string | null, count: number) => {
    setSkills(skills.map((s) => {
      if (s.skill_name !== skillName) return s
      const key = (e: SkillRubric['question_mix'][number]) =>
        e.question_type_id === typeId && (e.question_subtype_id || null) === subtypeId
      if (count <= 0) {
        return { ...s, question_mix: s.question_mix.filter((e) => !key(e)) }
      }
      const exists = s.question_mix.some(key)
      return {
        ...s,
        question_mix: exists
          ? s.question_mix.map((e) => key(e) ? { ...e, count } : e)
          : [...s.question_mix, { question_type_id: typeId, question_subtype_id: subtypeId, count }],
      }
    }))
  }

  const getSkillMixCount = (skill: SkillRubric, typeId: string, subtypeId: string | null) =>
    skill.question_mix.find((e) => e.question_type_id === typeId && (e.question_subtype_id || null) === subtypeId)?.count || 0

  const getSkillMixLevel = (skill: SkillRubric, typeId: string, subtypeId: string | null) =>
    skill.question_mix.find((e) => e.question_type_id === typeId && (e.question_subtype_id || null) === subtypeId)?.complexity_level_id || null

  const setSkillMixLevel = (skillName: string, typeId: string, subtypeId: string | null, levelId: string | null) => {
    setSkills(skills.map((s) => s.skill_name !== skillName ? s : {
      ...s,
      question_mix: s.question_mix.map((e) =>
        e.question_type_id === typeId && (e.question_subtype_id || null) === subtypeId
          ? { ...e, complexity_level_id: levelId }
          : e),
    }))
  }

  // A skill must ask at least one question. Otherwise its question mix is
  // saved empty, and on the next reload the auto-fill above (which fills
  // EMPTY mixes from the JD suggestion) brings back the AI-suggested counts,
  // which looks like the old values reappearing. Checked on Save and on
  // Generate; the message stays on the skill until it's fixed or removed.
  const skillsWithoutQuestions = skills
    .filter((s) => !s.question_mix.some((e) => e.count > 0))
    .map((s) => s.skill_name)
  const [showMixErrors, setShowMixErrors] = useState(false)
  const mixIsValid = () => {
    if (skillsWithoutQuestions.length === 0) return true
    setShowMixErrors(true)
    toast.error(
      skillsWithoutQuestions.length === 1
        ? `"${skillsWithoutQuestions[0]}" has no questions. Add at least one, or remove the skill.`
        : `${skillsWithoutQuestions.length} skills have no questions. Add at least one to each, or remove them.`,
    )
    return false
  }

  // Tells the page whether there are edits not yet saved, so adding a
  // question (which reloads this editor from the last save) can warn first.
  // Same defaults as the initial `skills` above, so filling in default
  // weights doesn't count as an unsaved edit.
  const [savedSnapshot] = useState(() => JSON.stringify(
    skillsRubrics.map((s) => ({ ...s, rubric_criteria: withDefaultWeights(s.rubric_criteria || []), question_mix: s.question_mix || [] })),
  ))
  useEffect(() => {
    onDirtyChange(JSON.stringify(skills) !== savedSnapshot)
  }, [skills, savedSnapshot, onDirtyChange])
  useEffect(() => () => onDirtyChange(false), [onDirtyChange])

  const handleSaveAndContinue = () => {
    if (!mixIsValid()) return
    onSave({ skills_rubrics: skills })
  }

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-7">
      <div className="sticky top-0 z-20 flex items-center justify-between gap-3 -mx-6 -mt-6 px-6 py-3 border-b border-gray-200 bg-gray-50/95 backdrop-blur-sm shadow-sm rounded-t-lg">
        <button
          onClick={onExtractContext}
          disabled={extracting}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 text-blue-700 rounded-lg text-xs font-medium hover:bg-blue-100 disabled:opacity-50"
        >
          {extracting ? <LoadingSpinner size="sm" /> : <SparklesIcon className="w-3.5 h-3.5" />}
          {extracting ? 'Extracting…' : contextExtractedAt ? 'Re-extract from JD' : 'Extract Context from JD'}
        </button>
        <div className="flex items-center gap-2">
          <button
            onClick={handleSaveAndContinue}
            disabled={saving || generating}
            className="inline-flex items-center gap-1.5 px-3 py-2 border border-gray-300 rounded-lg text-sm text-gray-700 hover:bg-gray-50 disabled:opacity-50 bg-white"
          >
            {saving && <LoadingSpinner size="sm" />}
            {saving ? 'Saving…' : 'Save Progress'}
          </button>
          <button
            onClick={() => { if (mixIsValid()) onGenerate({ skills_rubrics: skills }) }}
            disabled={generating || saving || skills.length === 0 || !hasAnyQuestions}
            className="inline-flex items-center gap-1.5 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 disabled:opacity-50 shadow-sm"
          >
            {generating ? <LoadingSpinner size="sm" /> : <CheckCircleIcon className="w-4 h-4" />}
            {generating ? 'Generating Questions… this can take a moment' : 'Generate Question Bank'}
          </button>
        </div>
      </div>

      {onCancelEdit && (
        <button onClick={onCancelEdit} className="text-xs text-gray-500 hover:text-gray-700">
          ← Back to summary
        </button>
      )}

      {/* Step: Context extraction */}
      <div>
        <h3 className="flex items-center gap-2.5 text-sm font-semibold text-gray-900 mb-3">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">1</span>
          Job Context
        </h3>
        {extracting ? (
          <div className="text-xs text-gray-500 bg-gray-50 rounded-lg p-3.5 flex items-center gap-2 ml-8">
            <LoadingSpinner size="sm" /> Reading the job description and identifying key skills…
          </div>
        ) : contextExtractedAt ? (
          <div className="text-xs text-gray-600 bg-gray-50 rounded-lg p-3.5 space-y-1 ml-8">
            <p><span className="font-medium">Role:</span> {jdContext.role_title} ({jdContext.seniority_level})</p>
            {jdContext.summary && <p>{jdContext.summary}</p>}
          </div>
        ) : (
          <p className="text-xs text-gray-400 ml-8">Not extracted yet — this pre-fills skill and rubric suggestions below.</p>
        )}
      </div>

      {/* Step: Skills, rubrics & questions (merged — each skill carries its own
          rubric criteria AND the question type/subtype mix that assesses it) */}
      <div>
        <h3 className="flex items-center gap-2.5 text-sm font-semibold text-gray-900 mb-3">
          <span className="w-6 h-6 rounded-full bg-blue-600 text-white text-xs font-bold flex items-center justify-center flex-shrink-0">2</span>
          Skills, Rubrics &amp; Questions
          <span className="text-xs font-normal text-gray-400">
            ({skills.length}/{MAX_SKILLS} skills{/* · ~{totalMinutesEstimate} min total */})
          </span>
        </h3>

        {justExtractedMix && (
          <div className="lg:ml-8 mb-4 flex items-start gap-2 bg-indigo-50 border border-indigo-100 rounded-lg px-3.5 py-2.5">
            <SparklesIcon className="w-4 h-4 text-indigo-500 flex-shrink-0 mt-0.5" />
            <p className="text-xs text-indigo-800 flex-1">
              Question counts below are AI-suggested from the job description — each skill only got counts for
              question types that make sense for it (e.g. no coding questions for a purely verbal skill). Review
              and adjust before saving.
            </p>
            <button onClick={onDismissJustExtractedMix} className="text-indigo-400 hover:text-indigo-600 flex-shrink-0">
              <XMarkIcon className="w-3.5 h-3.5" />
            </button>
          </div>
        )}

        <div className="space-y-4 lg:ml-8">
          {skills.map((skill) => {
            const questionCount = skillQuestionCount(skill)
            const eligibleTypeIds = eligibleTypeIdsForSkill(skill)
            return (
              <div key={skill.skill_name} className="border border-gray-200 rounded-lg overflow-hidden hover:border-blue-200 transition-colors">
                <div className="flex items-center justify-between px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <div className="flex items-center gap-2.5">
                    <span className="text-sm font-semibold text-gray-900">{skill.skill_name}</span>
                    {/* <span className="text-xs font-normal text-gray-400">~{skillMinutes(skill, questionTypes)} min</span> */}
                    <span className={`text-[11px] px-2 py-0.5 rounded-full font-medium ${
                      questionCount > 0 ? 'bg-blue-50 text-blue-700' : 'bg-gray-100 text-gray-400'
                    }`}>
                      {questionCount} question{questionCount === 1 ? '' : 's'}
                    </span>
                  </div>
                  <button onClick={() => removeSkill(skill.skill_name)} className="text-gray-400 hover:text-red-500">
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>

                <div className="p-4 space-y-4">
                  {/* Rubric criteria — full width so this section's row count never
                      visually implies any relationship to the question-mix chips below it. */}
                  <div>
                    <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide mb-2">Rubric</p>
                    <div className="space-y-1.5">
                      {skill.rubric_criteria.map((c, idx) => (
                        <div key={idx} className="flex gap-2 items-start">
                          <input
                            value={c.name}
                            onChange={(e) => updateCriterion(skill.skill_name, idx, 'name', e.target.value)}
                            placeholder="Criterion name"
                            className="flex-1 min-w-0 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                          <input
                            value={c.description || ''}
                            onChange={(e) => updateCriterion(skill.skill_name, idx, 'description', e.target.value)}
                            placeholder="What a strong answer shows"
                            className="flex-1 min-w-0 border border-gray-200 rounded-md px-2.5 py-1.5 text-xs focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                          />
                          {/* This criterion's share of the skill's 100 points.
                              Shown on the scorecard as the denominator, e.g. 20/30. */}
                          <div className="relative flex-shrink-0">
                            <input
                              type="number"
                              min={0}
                              max={100}
                              value={c.weight ?? ''}
                              onChange={(e) => updateCriterion(
                                skill.skill_name, idx, 'weight',
                                e.target.value === '' ? null : Number(e.target.value),
                              )}
                              placeholder="—"
                              title="Share of this skill's 100 points"
                              className="w-16 border border-gray-200 rounded-md pl-2 pr-5 py-1.5 text-xs text-right focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
                            />
                            <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] text-gray-400 pointer-events-none">%</span>
                          </div>
                          <button onClick={() => removeCriterion(skill.skill_name, idx)} className="text-gray-300 hover:text-red-500 mt-1.5">
                            <TrashIcon className="w-3.5 h-3.5" />
                          </button>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2 flex items-center justify-between gap-2">
                      <button
                        onClick={() => addCriterion(skill.skill_name)}
                        className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
                      >
                        <PlusIcon className="w-3 h-3" /> Add rubric criterion
                      </button>
                      <RubricWeightTotal criteria={skill.rubric_criteria} />
                    </div>
                  </div>

                  {/* Question type/subtype mix — one chip per question type (or, for a
                      type with subtypes, one grouped card with a mini chip per subtype),
                      colored by category so "which types does this skill actually use"
                      reads at a glance across several skill cards. */}
                  <div className="pt-3.5 border-t border-gray-100">
                    <div className="flex items-center justify-between gap-2 mb-2">
                      <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Questions for this skill</p>
                      {/* {jdContext?.candidate_skills?.length > 0 && (
                        <select
                          value=""
                          onChange={(e) => { if (e.target.value) applySuggestedMixToSkill(skill.skill_name, e.target.value) }}
                          className="text-[11px] border border-gray-200 rounded-md px-1.5 py-1 text-gray-500 bg-white focus:outline-none focus:ring-2 focus:ring-blue-100 max-w-[140px]"
                        >
                          <option value="">✨ Apply suggestion…</option>
                          {jdContext.candidate_skills.map((c: any) => (
                            <option key={c.skill_name} value={c.skill_name}>{c.skill_name}</option>
                          ))}
                        </select>
                      )} */}
                    </div>
                    {showMixErrors && skillsWithoutQuestions.includes(skill.skill_name) && (
                      <p role="alert" className="text-xs text-red-700 bg-red-50 border border-red-100 rounded-md px-3 py-2 mb-2">
                        Add at least 1 question (Coding, Design or Verbal) for this skill, or remove the skill.
                      </p>
                    )}
                    {questionTypes.length === 0 ? (
                      <p className="text-xs text-gray-400">No question types configured for this organization yet.</p>
                    ) : (
                      <div className="flex flex-wrap gap-2">
                        {questionTypes.map((type) => {
                          const meta = categoryMeta(type.category)
                          const Icon = meta.icon
                          const isEligible = eligibleTypeIds === null || eligibleTypeIds.has(type.id)
                          if (type.subtypes.length === 0) {
                            const count = getSkillMixCount(skill, type.id, null)
                            return (
                              <div
                                key={type.id}
                                className={`flex flex-col gap-2 rounded-lg border px-2.5 py-2 self-start min-w-[190px] ${
                                  !isEligible ? 'bg-gray-50 border-gray-200 opacity-50' : count > 0 ? meta.classes : 'bg-white border-gray-200'
                                }`}
                                title={!isEligible ? 'Not suggested for this skill' : `Response mode: ${type.response_mode}`}
                              >
                                <div className="flex items-center gap-2.5 justify-between">
                                  <div className="flex items-center gap-2.5 min-w-0">
                                    <Icon className={`w-4 h-4 flex-shrink-0 ${count > 0 && isEligible ? '' : 'text-gray-400'}`} />
                                    <div className="min-w-0 leading-tight">
                                      <p className={`text-xs font-medium truncate ${count > 0 && isEligible ? '' : 'text-gray-500'}`}>{type.name}</p>
                                      <p className="text-[10px] opacity-70 truncate">{type.default_duration_minutes} min</p>
                                    </div>
                                  </div>
                                  <MixStepper
                                    count={count}
                                    onChange={(next) => setSkillMixCount(skill.skill_name, type.id, null, next)}
                                    disableIncrement={!isEligible}
                                  />
                                </div>
                                {count > 0 && (
                                  <div className="pt-1.5 border-t border-black/5">
                                    <LevelSelect
                                      levels={complexityLevels}
                                      value={getSkillMixLevel(skill, type.id, null)}
                                      onChange={(next) => setSkillMixLevel(skill.skill_name, type.id, null, next)}
                                    />
                                  </div>
                                )}
                              </div>
                            )
                          }
                          // A suggested/manually-set count with no subtype (question_subtype_id:
                          // null) is perfectly valid data — the question schema allows a
                          // subtype-less question for any type — but until now this view only
                          // rendered one row per REAL subtype, so a null-subtype count was
                          // seeded/saved correctly yet had no row to display or edit it in. The
                          // "General" row below is that missing row, always shown so it's never
                          // silently invisible again.
                          const generalCount = getSkillMixCount(skill, type.id, null)
                          const groupCount = generalCount + type.subtypes.reduce(
                            (sum, sub) => sum + getSkillMixCount(skill, type.id, sub.id), 0
                          )
                          return (
                            <div
                              key={type.id}
                              className={`flex flex-col gap-1.5 rounded-lg border px-2.5 py-2 self-start ${
                                !isEligible ? 'bg-gray-50 border-gray-200 opacity-50' : groupCount > 0 ? meta.classes : 'bg-white border-gray-200'
                              }`}
                              title={!isEligible ? 'Not suggested for this skill' : undefined}
                            >
                              <div className="flex items-center gap-1.5">
                                <Icon className={`w-3.5 h-3.5 flex-shrink-0 ${groupCount > 0 && isEligible ? '' : 'text-gray-400'}`} />
                                <p className={`text-xs font-medium ${groupCount > 0 && isEligible ? '' : 'text-gray-500'}`}>{type.name}</p>
                              </div>
                              <div className="flex flex-col gap-1 pl-1">
                                <div className="flex items-center gap-2.5 justify-between">
                                  <span className={`text-[11px] truncate ${generalCount > 0 ? '' : 'opacity-70'}`}>
                                    General <span className="opacity-70">(no specific subtype)</span>
                                  </span>
                                  <MixStepper
                                    count={generalCount}
                                    onChange={(next) => setSkillMixCount(skill.skill_name, type.id, null, next)}
                                    disableIncrement={!isEligible}
                                  />
                                </div>
                                {generalCount > 0 && (
                                  <div className="pl-2 pb-1 border-b border-black/5">
                                    <LevelSelect
                                      levels={complexityLevels}
                                      value={getSkillMixLevel(skill, type.id, null)}
                                      onChange={(next) => setSkillMixLevel(skill.skill_name, type.id, null, next)}
                                    />
                                  </div>
                                )}
                                {type.subtypes.map((sub) => {
                                  const count = getSkillMixCount(skill, type.id, sub.id)
                                  return (
                                    <div key={sub.id} className="flex flex-col gap-1">
                                      <div className="flex items-center gap-2.5 justify-between">
                                        <span className={`text-[11px] truncate ${count > 0 ? '' : 'opacity-70'}`}>
                                          {sub.name} <span className="opacity-70">({sub.default_duration_minutes ?? type.default_duration_minutes} min)</span>
                                        </span>
                                        <MixStepper
                                          count={count}
                                          onChange={(next) => setSkillMixCount(skill.skill_name, type.id, sub.id, next)}
                                          disableIncrement={!isEligible}
                                        />
                                      </div>
                                      {count > 0 && (
                                        <div className="pl-2 pb-1 border-b border-black/5">
                                          <LevelSelect
                                            levels={complexityLevels}
                                            value={getSkillMixLevel(skill, type.id, sub.id)}
                                            onChange={(next) => setSkillMixLevel(skill.skill_name, type.id, sub.id, next)}
                                          />
                                        </div>
                                      )}
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          )
                        })}
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )
          })}
        </div>
        {skills.length < MAX_SKILLS && (
          <div className="relative mt-3 lg:ml-8">
            <div className="flex gap-2">
              <input
                value={newSkillName}
                onChange={(e) => setNewSkillName(e.target.value)}
                onFocus={() => setShowSkillSuggestions(true)}
                onBlur={() => setTimeout(() => setShowSkillSuggestions(false), 150)}
                onKeyDown={(e) => e.key === 'Enter' && addSkill()}
                placeholder={
                  extractedSkillSuggestions.length > 0
                    ? 'Type a skill, or pick one extracted from the job description below'
                    : 'Add a skill to assess'
                }
                className="flex-1 min-w-0 border border-gray-300 rounded-lg px-3 py-1.5 text-sm focus:border-blue-400 focus:outline-none focus:ring-2 focus:ring-blue-100"
              />
              <button onClick={addSkill} className="px-3 py-1.5 bg-gray-100 text-gray-700 rounded-lg text-sm hover:bg-gray-200 flex-shrink-0">
                Add
              </button>
            </div>

            {showSkillSuggestions && filteredSuggestions.length > 0 && (
              <div className="absolute z-10 left-0 right-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg max-h-56 overflow-y-auto">
                <p className="px-3 pt-2 pb-1 text-[10px] font-semibold text-gray-400 uppercase tracking-wide">
                  From the job description
                </p>
                {filteredSuggestions.map((c: any) => (
                  <button
                    key={c.skill_name}
                    // onMouseDown (not onClick) fires before the input's onBlur closes
                    // this dropdown, so the click actually lands on the suggestion.
                    onMouseDown={(e) => { e.preventDefault(); addSkillFromSuggestion(c) }}
                    className="w-full text-left px-3 py-2 text-sm text-gray-700 hover:bg-blue-50 flex items-center justify-between gap-2"
                  >
                    <span className="truncate">{c.skill_name}</span>
                    <PlusIcon className="w-3.5 h-3.5 text-gray-400 flex-shrink-0" />
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  )
}
